import './index.css'

import cn from 'clsx'
import React, { useMemo } from 'react'
import { useMediaQuery } from 'react-responsive'
import type { Location } from 'react-router-dom'

import {
  CHANNELS_LIMITED,
  CHANNELS_UNLIMITED,
  NOTES_V2_MENTIONS,
  ONGOING_DDEX_VISIBLE,
  TRACK_NESTING,
  TRACK_NESTING_EXPORT,
} from '@disco/constants/FeatureTypes'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import {
  getIsTrial,
  useFeatureEnabled,
} from '@disco/state-management/selectors/business'
import {
  getConstanceSetting,
  isNativeTagsEnabled,
} from '@disco/state-management/selectors/user'

import DocumentHeadTags from 'components/document-head-tags'
import PageLayout from 'components/ui/page-layout'

import { useSelector } from 'hooks/use-redux'

type Props = {
  children: React.ReactNode
  location: Location
}

const Settings = ({ children, location }: Props): React.ReactElement<'div'> => {
  const channelsLimited: boolean = useFeatureEnabled(CHANNELS_LIMITED)
  const channelsUnlimited: boolean = useFeatureEnabled(CHANNELS_UNLIMITED)
  const channelsEnabled: boolean = channelsLimited || channelsUnlimited
  const trackNestingEnabled: boolean = useFeatureEnabled(TRACK_NESTING)
  const trackNestingExportEnabled: boolean =
    useFeatureEnabled(TRACK_NESTING_EXPORT)
  const nestingExportEnabled = trackNestingEnabled && trackNestingExportEnabled
  const DDEXEnabled: boolean = useFeatureEnabled(ONGOING_DDEX_VISIBLE)
  const isPhone: boolean = useSelector(isDeviceTypePhone)
  const isTablet = useMediaQuery({
    maxWidth: 1299,
  })
  const isTrial: boolean = useSelector(getIsTrial)
  const nativeTagsEnabled: boolean = useSelector(isNativeTagsEnabled)
  const notesMentionsEnabled: boolean = useSelector(state =>
    Boolean(getConstanceSetting(state, NOTES_V2_MENTIONS)),
  )
  const NAV_ITEMS = useMemo(
    () =>
      [
        {
          label: 'My Settings',
          expandable: true,
          items: [
            {
              label: 'Profile & Account',
              link: '/settings/account',
            },
            {
              label: 'Security',
              link: '/settings/security',
            },
            {
              label: 'Display Settings',
              link: '/settings/display',
            },
            notesMentionsEnabled && {
              label: 'Notifications',
              link: '/settings/notifications',
            },
          ].filter(Boolean),
        },
        {
          label: 'Workspace Settings',
          expandable: true,
          items: [
            {
              label: 'Business Settings',
              link: '/settings/business',
            },
            {
              label: 'Users',
              link: '/settings/users',
            },
            {
              label: 'Billing & Subscriptions',
              link: '/settings/billing',
            },
            {
              label: 'Metadata',
              link: '/settings/metadata',
            },
            nativeTagsEnabled && {
              label: 'Track Tags',
              link: '/settings/track-tags',
            },
            {
              label: 'Playlist Tags',
              link: '/settings/playlist-tags',
            },
            {
              label: 'Inboxes',
              link: '/settings/inboxes',
            },
            channelsEnabled && {
              label: 'Channels',
              link: '/settings/channels',
            },
            {
              label: 'Themes',
              link: '/settings/themes',
            },
          ].filter(Boolean),
        },
        (nestingExportEnabled || DDEXEnabled) && {
          label: 'Data & Sharing',
          expandable: true,
          items: [
            nestingExportEnabled && {
              label: 'Export Nesting CSV',
              link: '/settings/nesting-export',
            },
            DDEXEnabled && {
              label: 'DDEX',
              link: '/settings/ddex',
            },
          ].filter(Boolean),
        },
      ].filter(Boolean),
    [
      DDEXEnabled,
      channelsEnabled,
      nativeTagsEnabled,
      nestingExportEnabled,
      notesMentionsEnabled,
    ],
  )
  const trialBillingPage =
    isTrial && isTablet && location.pathname.startsWith('/settings/billing')
  const hideNav = isPhone || trialBillingPage
  return (
    <div className="Page">
      <DocumentHeadTags title="Settings | DISCO" />
      <div className="Page_Content">
        <PageLayout title="Settings" nav={hideNav ? null : NAV_ITEMS}>
          {children}
        </PageLayout>
      </div>
    </div>
  )
}

export default Settings
